import { useRef, useState } from 'react'
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params'
import { Icon } from '@iconify/react'
import { Spin, useMessage } from '@pepper/ui'
import { pluralize } from '@pepper/utils/src/function/pluralize'
import { Domain } from '@pepper/utils/src/types/seo'

import BottomPopup, { BottomPopupBtn } from 'components/BottomPopup'
import ContentIdeasDrawer from 'components/content-ideas/ContentIdeasDrawer'

import Table from './Table'
import Filter from './Filter'
import EmptyState from './EmptyState'
import ImportModal from './ImportModal'
import IdeaLabDrawer from '../IdeaLabDrawer'
import TopicSuggestionsTable from '../IdeaLabDrawer/TopicSuggestionsTable'
import AppliedFilters from './Filter/AppliedFilters'
import LabRouteSwitch from '../LabRouteSwitch'
import CreateKeywordsDropdown from './CreateKeywordsDropdown'
import { ExportButton } from '../ExportButton/ExportButton'

import { useProjectKeywords } from 'utils/hooks/useProjectKeywords'
import { useDeleteProjectKeywords } from 'utils/hooks/useDeleteProjectKeywords'

import { Keyword } from 'types/keyword'

import s from './Keywords.module.css'

type Props = {
  projectId: string
  companyId: string
  domain?: Domain
}

type Intent = 0 | 1 | 2 | 3

export const Keywords = ({ companyId, projectId, domain }: Props) => {
  const [queryParams, setQueryParams] = useQueryParams({
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, 25),
    sug_page: withDefault(NumberParam, 1),
    sug_limit: withDefault(NumberParam, 15),
    content_ideas: withDefault(BooleanParam, false),
    keywords_to_topics: withDefault(BooleanParam, false),
    isImportModalOpen: withDefault(BooleanParam, false),
    keyword: withDefault(StringParam, undefined),
    intent: withDefault(NumberParam, undefined),
    'volume:lte': withDefault(NumberParam, undefined),
    'volume:gte': withDefault(NumberParam, undefined),
    'difficulty:lte': withDefault(NumberParam, undefined),
    'difficulty:gte': withDefault(NumberParam, undefined),
    sort: withDefault(StringParam, undefined),
    positionGte: withDefault(NumberParam, undefined),
    positionLte: withDefault(NumberParam, undefined),
    exclude_position: withDefault(BooleanParam, false),
  })
  const queryParamsFilter = {
    keyword: queryParams.keyword,
    intent: queryParams.intent as Intent,
    'difficulty:gte': queryParams['difficulty:gte'],
    'difficulty:lte': queryParams['difficulty:lte'],
    'volume:lte': queryParams['volume:lte'],
    'volume:gte': queryParams['volume:gte'],
    sort: queryParams.content_ideas ? undefined : queryParams.sort,
    positionLte: queryParams.positionLte,
    positionGte: queryParams.positionGte,
    exclude_position: queryParams.exclude_position,
  }
  const appliedQueryParamsFilterAndSort = useRef(queryParamsFilter)

  const [selectedThemeKeywordIds, setSelectedThemeKeywordIds] =
    useState<string[]>()
  const [selectedRows, setSelectedRows] = useState<React.Key[]>([])
  const [isAddingBulkKeywords, setIsAddingBulkKeywords] = useState(false)
  const { showError, showSuccess } = useMessage()

  const { data: keywords, isLoading } = useProjectKeywords(
    (() => {
      const {
        'difficulty:gte': gteDifficulty,
        'difficulty:lte': lteDifficulty,
        'volume:lte': lteVolume,
        'volume:gte': gteVolume,
        exclude_position,
        ...rest
      } = queryParamsFilter
      return {
        projectId,
        companyId,
        page: queryParams.page,
        limit: queryParams.limit,
        queryParamsFilter: {
          ...rest,
          keyword: rest.keyword?.split(','),
          exists: exclude_position,
          gteDifficulty,
          gteVolume,
          lteDifficulty,
          lteVolume,
        },
      }
    })()
  )

  const { mutateAsync: deleteKeywords, isLoading: isDeleting } =
    useDeleteProjectKeywords()

  const isFilterApplied =
    !!queryParams['difficulty:gte'] ||
    !!queryParams['difficulty:lte'] ||
    !!queryParams['volume:gte'] ||
    !!queryParams['volume:lte'] ||
    !!queryParams.intent ||
    !!queryParams.keyword?.length ||
    queryParams.exclude_position
  const isDomainRemoved = !!domain?.deleted_at
  const isDomainNotLinked = !domain

  const handleRowSelect = (selectedRow: React.Key[]) => {
    setSelectedRows([...selectedRow])
  }

  const handleCreateTopics = (themeKeywordId?: string) => {
    if (themeKeywordId) {
      setSelectedThemeKeywordIds([themeKeywordId])
    } else {
      const themeKeywordIds = keywords?.records
        .filter(keyword => selectedRows.includes(keyword.id))
        .map(keyword => keyword.theme_keyword_id)
      setSelectedThemeKeywordIds(themeKeywordIds)
    }

    appliedQueryParamsFilterAndSort.current = { ...queryParamsFilter }
    setQueryParams({ keywords_to_topics: true }, 'push')
  }

  const handleDeleteKeywords = async (_keyword?: Keyword) => {
    const keywordIds = _keyword ? [_keyword.id] : selectedRows.map(String)
    const { status, message } = await deleteKeywords({
      companyId,
      projectId,
      keywordIds,
    })
    if (status === 'success') {
      showSuccess(
        `${pluralize(keywordIds.length, 'keyword')} deleted successfully`
      )
      setSelectedRows([])
    } else {
      showError(message)
    }
  }

  return (
    <Spin spinning={isAddingBulkKeywords}>
      <div className={s.wrapper}>
        <div className={s.header}>
          <LabRouteSwitch />

          <div className={s.filterWrapper}>
            {!keywords?.total_records ? null : (
              <ExportButton
                title="Export Keywords"
                isSelection={selectedRows.length > 0}
                selectedKeywords={keywords?.records.filter(keyword =>
                  selectedRows.includes(keyword.id)
                )}
                type="keyword"
                projectId={projectId}
                companyId={companyId}
              />
            )}

            {(!!keywords?.total_records || isFilterApplied) && <Filter />}
            <CreateKeywordsDropdown
              onResearchKeywords={() => setQueryParams({ content_ideas: true })}
              onImportKeywords={() => {
                setQueryParams({ isImportModalOpen: true })
              }}
            />
          </div>
        </div>
        {isFilterApplied && <AppliedFilters />}

        {((!isLoading && keywords?.total_records === 0 && !isFilterApplied) ||
          isDomainNotLinked) && (
          <EmptyState
            isDomainNotLinked={isDomainNotLinked}
            isDomainRemoved={isDomainRemoved}
            onResearchKeywords={() => setQueryParams({ content_ideas: true })}
            onImportKeywords={() => {
              setQueryParams({ isImportModalOpen: true })
            }}
          />
        )}

        {!isLoading && keywords?.total_records === 0 && isFilterApplied && (
          <div className={s.emptyList}>No keywords found</div>
        )}

        {keywords?.total_records !== 0 && !isDomainNotLinked && (
          <div className={s.body}>
            <Table
              loading={isLoading}
              totalRecords={keywords?.total_records ?? 0}
              dataSource={keywords?.records}
              onSelect={handleRowSelect}
              rowsSelected={selectedRows}
              onCreateTopics={handleCreateTopics}
              onDeleteKeyword={handleDeleteKeywords}
              page={queryParams.page}
              total={keywords?.total_records ?? 0}
              isDomainRemoved={isDomainRemoved}
              onPageChange={page => {
                setQueryParams({
                  page,
                })
                setSelectedRows([])
              }}
              limit={queryParams.limit}
              onPageSizeChange={pageSize =>
                setQueryParams({
                  limit: pageSize,
                  page: 1,
                })
              }
              projectId={projectId}
            />
            <BottomPopup
              selectedCount={selectedRows.length}
              onClose={() => {
                setSelectedRows([])
              }}
            >
              <BottomPopupBtn
                onClick={() => handleCreateTopics()}
                disabled={isDomainRemoved}
                icon={<Icon icon="lucide:plus" />}
              >
                Create Topics
              </BottomPopupBtn>
              <BottomPopupBtn
                type="delete"
                onClick={() => handleDeleteKeywords()}
                isLoading={isDeleting}
              />
            </BottomPopup>
          </div>
        )}
      </div>
      {/* Domain is required for IdeaLabDrawer and it's subcomponents */}
      {!!domain && (
        <>
          {!!domain && (
            <ContentIdeasDrawer
              domain={domain}
              companyId={companyId}
              projectId={projectId}
            />
          )}
          <IdeaLabDrawer
            title={
              <div className={s.title}>
                <Icon
                  icon="heroicons:sparkles-solid"
                  width={18}
                  height={18}
                  className={s.icon}
                />
                Topics from Keywords
              </div>
            }
            open={queryParams.keywords_to_topics}
            onClose={() =>
              setQueryParams({
                ...appliedQueryParamsFilterAndSort.current,
                keywords_to_topics: undefined,
                sug_page: undefined,
                sug_limit: undefined,
              })
            }
          >
            <div className={s.drawerContent}>
              <p className={s.copy}>
                Select and add the topics you want. You can also edit the topics
                and add them to the project.
              </p>
              <TopicSuggestionsTable
                companyId={companyId}
                projectId={projectId}
                domainId={domain.id}
                domainRegion={domain.region_code}
                themeKeywordIds={selectedThemeKeywordIds}
                isDomainRemoved={isDomainRemoved}
              />
            </div>
          </IdeaLabDrawer>
        </>
      )}
      <ImportModal
        companyId={companyId}
        projectId={projectId}
        onClose={() => setQueryParams({ isImportModalOpen: undefined })}
        open={!!queryParams.isImportModalOpen}
        setIsAddingBulkKeywords={setIsAddingBulkKeywords}
      />
    </Spin>
  )
}
